import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import authStorage from "setup/redux/authStorage";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { IClientPortalCPLSettingsService } from "services/ClientPortalCPLSettingsService";
import { IAuthenticationService } from "services/AuthenticationService";
import { CPLAuthTokenModel } from "services/api/cplauthapi/CPLAuthAPI";
import { actions, actionTypes } from "../../redux/AuthRedux";
import { setGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import { toast } from "react-toastify";
import { RootState } from "setup/redux/RootReducer";

export const impersonationHandoffQueryParam = "ref";

type ImpersonationState = {
  IsImpersonating: boolean;
  ImpersonatedUserName: string | undefined;
  ImpersonatorToken: CPLAuthTokenModel | undefined;
};

const initialState: ImpersonationState = {
  IsImpersonating: false,
  ImpersonatedUserName: undefined,
  ImpersonatorToken: undefined,
};

export const impersonationSlice = createSlice({
  name: "impersonation",
  initialState,
  reducers: {
    setIsImpersonating: (state, action) => {
      state.IsImpersonating = action.payload;
    },
    setImpersonatedUserName: (state, action) => {
      state.ImpersonatedUserName = action.payload;
    },
    setImpersonatorToken: (state, action) => {
      state.ImpersonatorToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionTypes.Logout, () => initialState);
  },
});

export type ImpersonateUserArgs = {
  verificationCode: string;
  handoffReference: string;
};

export const impersonateUser = createAsyncThunk<boolean, ImpersonateUserArgs>(
  "Impersonation/ImpersonateUser",
  async ({ verificationCode, handoffReference }, thunkAPI) => {
    const authenticationService = container.get<IAuthenticationService>(SERVICE_TYPES.AuthenticationService);
    const impersonationToken = await authenticationService.redeemImpersonationHandoff(handoffReference);

    thunkAPI.dispatch(
      actions.impersonate(
        { impersonatedUserToken: impersonationToken, verificationCode },
        {
          errorHandler: () => {
            toast.error(
              "Unable to start impersonation session. This may mean the link has expired or already been used."
            );
            thunkAPI.dispatch(setIsImpersonating(false));
            thunkAPI.dispatch(setImpersonatedUserName(undefined));
            thunkAPI.dispatch(setImpersonatorToken(undefined));
          },
          successHandler: async () => {
            // Clear out any previous session's dashboard/profile/etc. state so it doesn't
            // briefly (or persistently) show through for the newly impersonated user.
            thunkAPI.dispatch(actions.resetContentState());

            const clientPortalCPLSettingsService = container.get<IClientPortalCPLSettingsService>(
              SERVICE_TYPES.ClientPortalCPLSettingsService
            );
            const globalSettings = await clientPortalCPLSettingsService.getGlobalClientPortalSettings();
            thunkAPI.dispatch(setGlobalSettings(globalSettings));
            thunkAPI.dispatch(setIsImpersonating(true));
            thunkAPI.dispatch(setImpersonatedUserName(impersonationToken.impersonatedUserName));
            thunkAPI.dispatch(setImpersonatorToken(impersonationToken.impersonatorUserToken));
          },
        }
      )
    );

    return true;
  }
);
export const { setIsImpersonating, setImpersonatedUserName, setImpersonatorToken } = impersonationSlice.actions;

export const selectIsImpersonating = (state: RootState) => state.impersonation.IsImpersonating;
export const selectImpersonatedUserName = (state: RootState) => state.impersonation.ImpersonatedUserName;
export const selectImpersonatorToken = (state: RootState) => state.impersonation.ImpersonatorToken;

export default persistReducer(
  {
    storage: authStorage,
    key: "cpl-client-portal-impersonation",
    whitelist: ["IsImpersonating", "ImpersonatedUserName", "ImpersonatorToken"],
  },
  impersonationSlice.reducer
);
